/**
 * Shape and reader for thin-content paragraph rewrites.
 *
 * Lives outside the component file so it can be imported by the card, the
 * drawer's apply gate and tests without dragging a component in (and so the
 * component module exports components only, per react-refresh).
 */

/**
 * Colour tone for a word-count readout: red while far short, amber while short,
 * teal once the threshold is met.
 *
 * Shared so the static "Word count" card and the live projected counter above
 * the paragraph edits cannot drift apart — two copies of this would eventually
 * disagree about what "short" looks like, on the same screen.
 */
export function getWordCountTone(wordCount: number, threshold: number) {
  if (wordCount < Math.min(100, threshold)) {
    return {
      bar: "bg-coral",
      text: "text-coral-dark",
      badge: "bg-coral-light text-coral-dark border-coral/30",
    };
  }
  if (wordCount < threshold) {
    return {
      bar: "bg-coral/70",
      text: "text-coral-dark",
      badge: "bg-coral-light/70 text-coral-dark border-coral/20",
    };
  }
  return {
    bar: "bg-teal",
    text: "text-teal-dark",
    badge: "bg-teal-light text-teal-dark border-teal/30",
  };
}

export interface ParagraphDiff {
  block_index: number;
  original: string;
  proposed: string;
  words_before?: number;
  words_after?: number;
}

/**
 * The paragraph rewrites carried by an expansion packet.
 *
 * Returns [] for anything malformed rather than throwing: a packet generated
 * before paragraph review existed simply has none, and that has to read as
 * "nothing to apply", not as an error.
 */
export function readParagraphDiffs(
  fix: Record<string, unknown> | null | undefined,
): ParagraphDiff[] {
  const raw = fix?.paragraph_diffs;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is ParagraphDiff =>
      Boolean(d) &&
      typeof d === "object" &&
      typeof (d as ParagraphDiff).original === "string" &&
      typeof (d as ParagraphDiff).proposed === "string" &&
      (d as ParagraphDiff).proposed.trim().length > 0,
  );
}
